/**
 * Minecraft-style chat system.
 * Opens with Enter key (caller handles keydown).
 * Supports player, system, and error messages with auto-fade.
 */
export class ChatSystem {
  constructor() {
    /** @type {Array<{text: string, type: string, time: number, el: HTMLDivElement}>} */
    this._messages = [];
    this._maxMessages = 50;
    this._maxVisible = 10;
    this._fadeDelay = 5;      // seconds before fade starts
    this._fadeDuration = 1;   // seconds for fade animation
    this._scrollOffset = 0;

    /** @type {((text: string) => void)|null} */
    this.onSubmit = null;

    this._build();
  }

  _build() {
    // Main container
    this.container = document.createElement('div');
    this.container.id = 'chat-container';
    this.container.style.cssText = `
      position: fixed;
      bottom: 60px;
      left: 10px;
      width: 400px;
      z-index: 700;
      pointer-events: none;
      font-family: 'Courier New', monospace;
      font-size: 14px;
    `;

    // Messages area
    this.messagesEl = document.createElement('div');
    this.messagesEl.id = 'chat-messages';
    this.messagesEl.style.cssText = `
      max-height: ${this._maxVisible * 22}px;
      overflow: hidden;
      display: flex;
      flex-direction: column;
      justify-content: flex-end;
      padding: 4px 6px;
      border-radius: 2px;
    `;
    this.container.appendChild(this.messagesEl);

    // Input field
    this.inputEl = document.createElement('input');
    this.inputEl.id = 'chat-input';
    this.inputEl.type = 'text';
    this.inputEl.maxLength = 200;
    this.inputEl.autocomplete = 'off';
    this.inputEl.spellcheck = false;
    this.inputEl.placeholder = 'Escribe un mensaje...';
    this.inputEl.style.cssText = `
      display: none;
      width: 100%;
      box-sizing: border-box;
      background: rgba(0, 0, 0, 0.6);
      border: 1px solid rgba(255, 255, 255, 0.2);
      border-radius: 2px;
      outline: none;
      color: white;
      font-family: 'Courier New', monospace;
      font-size: 14px;
      padding: 6px 8px;
      margin-top: 2px;
      text-shadow: 1px 1px 0 rgba(0, 0, 0, 0.8);
      pointer-events: auto;
    `;
    this.inputEl.addEventListener('keydown', (e) => this._onInputKey(e));
    this.container.appendChild(this.inputEl);

    document.body.appendChild(this.container);

    this._inputVisible = false;
  }

  // ---- Public API ----

  /** Whether the chat input is currently open */
  get isOpen() {
    return this._inputVisible;
  }

  /** Open chat input and focus it */
  open() {
    if (this._inputVisible) return;
    this._inputVisible = true;
    this.inputEl.style.display = 'block';
    this.inputEl.value = '';
    this._scrollOffset = 0;
    // Show messages background when chat is open
    this.messagesEl.style.background = 'rgba(0, 0, 0, 0.4)';
    // Make all current messages fully visible while chat is open
    this._refreshMessageVisibility();
    setTimeout(() => this.inputEl.focus(), 30);
  }

  /** Close chat input and return control to game */
  close() {
    if (!this._inputVisible) return;
    this._inputVisible = false;
    this.inputEl.style.display = 'none';
    this.inputEl.blur();
    this._scrollOffset = 0;
    // Hide messages background when chat is closed
    this.messagesEl.style.background = 'transparent';
    // Restore natural fade state
    this._refreshMessageVisibility();
  }

  /**
   * Add a message to chat.
   * @param {string} text
   * @param {'player'|'system'|'error'} type
   */
  sendMessage(text, type = 'player') {
    const color = this._getColor(type);

    // Create DOM element
    const el = document.createElement('div');
    el.className = 'chat-msg';
    el.style.cssText = `
      color: ${color};
      padding: 1px 0;
      line-height: 1.4;
      text-shadow: 1px 1px 0 rgba(0, 0, 0, 0.9);
      transition: opacity ${this._fadeDuration}s ease;
      opacity: 1;
      word-wrap: break-word;
    `;

    // Format text based on type
    if (type === 'player') {
      el.textContent = `<Jugador> ${text}`;
    } else if (type === 'system') {
      el.textContent = `[Sistema] ${text}`;
    } else if (type === 'error') {
      el.textContent = `[Error] ${text}`;
    } else {
      el.textContent = text;
    }

    // Store message data
    const msg = {
      text,
      type,
      time: 0,  // seconds since creation
      el,
      fading: false,
      faded: false,
    };
    this._messages.push(msg);

    // Trim to max history
    while (this._messages.length > this._maxMessages) {
      const removed = this._messages.shift();
      if (removed.el.parentNode) {
        removed.el.parentNode.removeChild(removed.el);
      }
    }

    // Add to DOM
    this.messagesEl.appendChild(el);

    // Keep only last _maxVisible visible in the scroll area
    this._trimVisibleMessages();

    // Auto-scroll to bottom
    this.messagesEl.scrollTop = this.messagesEl.scrollHeight;
  }

  /** Shortcut for system messages (yellow) */
  addSystemMessage(text) {
    this.sendMessage(text, 'system');
  }

  /**
   * Update fade timers. Call every frame.
   * @param {number} dt - delta time in seconds
   */
  update(dt) {
    for (const msg of this._messages) {
      msg.time += dt;

      // If chat is open, keep all messages visible
      if (this._inputVisible) {
        msg.el.style.opacity = '1';
        continue;
      }

      // Start fading after fadeDelay
      if (!msg.faded && msg.time >= this._fadeDelay && !msg.fading) {
        msg.fading = true;
        msg.el.style.opacity = '0';
      }

      // Mark as fully faded after transition completes
      if (msg.fading && msg.time >= this._fadeDelay + this._fadeDuration) {
        msg.faded = true;
        msg.fading = false;
      }
    }
  }

  /** Clean up DOM */
  dispose() {
    if (this.container && this.container.parentNode) {
      this.container.parentNode.removeChild(this.container);
    }
    this._messages = [];
  }

  // ---- Private ----

  _getColor(type) {
    switch (type) {
      case 'system': return '#FFFF55';   // yellow
      case 'error':  return '#FF5555';   // red
      case 'player':
      default:       return '#FFFFFF';   // white
    }
  }

  _onInputKey(e) {
    e.stopPropagation(); // Don't trigger game keybinds

    if (e.code === 'Enter') {
      const text = this.inputEl.value.trim();
      if (text && this.onSubmit) {
        this.onSubmit(text);
      } else if (text) {
        // If no handler, just show as player message
        this.sendMessage(text, 'player');
      }
      this.close();
    } else if (e.code === 'Escape') {
      this.close();
    } else if (e.code === 'ArrowUp') {
      e.preventDefault();
      this._scrollHistory(-1);
    } else if (e.code === 'ArrowDown') {
      e.preventDefault();
      this._scrollHistory(1);
    }
  }

  /** Scroll through visible messages when chat is open */
  _scrollHistory(direction) {
    if (!this._inputVisible) return;

    const totalMessages = this.messagesEl.children.length;
    if (totalMessages <= this._maxVisible) return;

    // direction: -1 = scroll up (older), +1 = scroll down (newer)
    if (direction < 0) {
      // Scroll up
      this.messagesEl.scrollTop = Math.max(0, this.messagesEl.scrollTop - 22);
    } else {
      // Scroll down
      this.messagesEl.scrollTop = Math.min(
        this.messagesEl.scrollHeight - this.messagesEl.clientHeight,
        this.messagesEl.scrollTop + 22
      );
    }
  }

  /** Keep DOM children trimmed so old faded messages don't pile up offscreen */
  _trimVisibleMessages() {
    // We keep all messages in the DOM for scrollback when chat is open.
    // The overflow:hidden + flex-end handles visual trimming.
    // If we have too many DOM nodes (beyond 2x max), remove oldest.
    while (this.messagesEl.children.length > this._maxMessages) {
      this.messagesEl.removeChild(this.messagesEl.firstChild);
    }
  }

  /** Refresh opacity of all messages based on current chat state */
  _refreshMessageVisibility() {
    for (const msg of this._messages) {
      if (this._inputVisible) {
        // Show all messages at full opacity when chat is open
        msg.el.style.opacity = '1';
      } else {
        // Restore natural fade state
        if (msg.faded || msg.time >= this._fadeDelay + this._fadeDuration) {
          msg.el.style.opacity = '0';
          msg.faded = true;
        } else if (msg.time >= this._fadeDelay) {
          msg.el.style.opacity = '0';
          msg.fading = true;
        } else {
          msg.el.style.opacity = '1';
        }
      }
    }
  }
}
